// Import necessary components
import React, { useState, useEffect } from "react";
import renderContent from "./inputFormAIHelpers/UploadDocumentRenderer";
import { manageDocumentSteps } from "../functions/documentStepManager"; // Adjust the path accordingly
import { areAllRequiredFilesUploaded } from "../functions/InputFormFunctions";
import "./UploadDocument.css";

const UploadDocument = ({
  userEmail,
  currentSection,
  company,
  collectionBrand,
  systemName,
  uniqueId,
  dataPointDefinitions,
  onDataPointsUpdate,
  formData,
}) => {
  const [state, setState] = useState({
    step: {
      current: "upload", // Represents the current step
      previous: null, // Represents the previous step
    },
    uploadedFiles: {},
    result: null,
    loadingLabel: "",
    documentStepMemory: {}, // Consolidated memory for all extracted data (e.g., uploadResults, auditRiskProfile, etc.)
    missingFiles: [], // Track missing required files
    documentSupportData: {}, // Store document support data for each document
  });

  const {
    step,
    uploadedFiles,
    result,
    loadingLabel,
    documentStepMemory,
    missingFiles,
    documentSupportData,
  } = state;

  useEffect(() => {
    if (documentStepMemory?.auditRiskProfile) {
      // If the auditRiskProfile is available, move to the next step
      setState((prevState) => ({
        ...prevState,
        step: {
          previous: prevState.step.current,
          current: "auditReview",
        },
      }));
    }
  }, [documentStepMemory]);
  // Handle file changes for document uploads
  const handleFileChange = (e, label) => {
    const { name, files } = e.target;
    if (files.length > 0) {
      const file = files[0];

      // Extract the file extension
      const fileExtension = file.name.split(".").pop().toLowerCase();

      // Enforce a naming convention with the uniqueId and include the extension
      const newFileName = `${name}_${uniqueId}.${fileExtension}`;
      const renamedFile = new File([file], newFileName, { type: file.type });

      setState((prevState) => ({
        ...prevState,
        uploadedFiles: {
          ...prevState.uploadedFiles,
          [name]: {
            file: renamedFile,
            label,
            fileType: fileExtension, // Add fileType here
          },
        },
      }));
    }
  };

  // Check current step to trigger step flow logic
  useEffect(() => {
    const steps = [
      "step1",
      "step2",
      "step3",
      "step4",
      "step4b",
      "step5",
      "step6",
      "step7",
      "step8",
    ];

    if (steps.includes(step.current)) {
      const totalDocuments = Object.keys(uploadedFiles).length;
      console.log("Total documents uploaded:", totalDocuments);
      manageDocumentSteps(
        step, // Pass the current step object
        documentStepMemory,
        (newMemory) => {
          setState((prevState) => ({
            ...prevState,
            documentStepMemory: newMemory,
          }));
        },
        setState,
        uploadedFiles,
        company,
        collectionBrand,
        systemName,
        uniqueId,
        userEmail,
        dataPointDefinitions,
        manageDocumentSteps,
        documentSupportData,
        totalDocuments
      );
    }
  }, [step.current]); // Update dependency to trigger on change of step.current

  // Handle document upload and analysis
  const handleDocumentUpload = async () => {
    // Check if all required files are uploaded using the helper function
    const allFilesUploaded = areAllRequiredFilesUploaded(
      currentSection,
      uploadedFiles
    );

    // If there are missing files, set error state and stop further processing
    if (!allFilesUploaded) {
      const missingFiles = currentSection?.fields
        .filter(
          (field) =>
            field.type === "file" &&
            field.required &&
            !uploadedFiles[field.name]
        )
        .map((field) => field.name);

      setState((prevState) => ({
        ...prevState,
        result: {
          type: "error",
          message: "Please upload all required documents before proceeding.",
        },
        step: {
          previous: prevState.step.current, // Set previous to the current value ("upload" or "uploadError")
          current: "uploadError", // Indicate an upload error state
        },
        missingFiles, // Track missing required files
      }));
      return; // Stop further processing if files are missing
    }

    // Create the documentSupportData object
    const documentSupportData = currentSection.fields.reduce((acc, field) => {
      if (field.type === "file") {
        acc[field.name] = {
          label: field.label,
          description: field.description,
          required: field.required,
        };
      }
      return acc;
    }, {});

    // Log the documentSupportData for verification

    // Update state to store the documentSupportData and move to the first step
    setState((prevState) => ({
      ...prevState,
      documentSupportData, // Store the documentSupportData in the state
      step: {
        previous: prevState.step.current,
        current: "step1", // Set current to the first step ("step1")
      },
    }));
  };

  return (
    <div
      className={`upload-document ${
        step.current === "processing" ? "loading-overlay" : ""
      }`}
    >
      {renderContent({
        step,
        currentSection,
        handleFileChange,
        handleDocumentUpload,
        uniqueId,
        uploadedFiles,
        missingFiles,
        result,
        documentStepMemory, // Pass documentStepMemory instead of individual states
        setState,
        loadingLabel,
        systemName,
        manageDocumentSteps,
        onCancel: () => {
          setState((prevState) => ({
            ...prevState,
            result: null,
            step: {
              previous: prevState.step.current, // Set previous to the current step before resetting
              current: "upload", // Reset current to "upload"
            },
          }));
        },
        onDataPointsUpdate,
        formData,
      })}
    </div>
  );
};

export default UploadDocument;